import { cn } from "utils/helpers";
import type { OverallSupplierScore } from "../../types/meeting.type";
import type { AwardStatus } from "../../../../../types/award.type";
import { CompanyLogoDisplay } from "newui/features/Company/components/CompanyLogo";

export type { AwardStatus } from "../../../../../types/award.type";

interface SupplierAwardCardProps {
  awardStatus: AwardStatus;
  comment: string | null;
  isBelowThreshold: boolean;
  isLeader: boolean;
  showAwardDecision: boolean;
  supplier: OverallSupplierScore;
}

const formatScore = (score: number | null): string => {
  if (score === null) {
    return "—";
  }
  return score.toFixed(2);
};

const AWARD_STATUS_BADGE: Record<AwardStatus, { className: string; label: string; }> = {
  pending: { label: "Pending", className: "bg-gray-100 text-gray-600" },
  awarded: { label: "Preferred", className: "bg-green-100 text-green-700" },
  unsuccessful: { label: "Unsuccessful", className: "bg-red-100 text-red-700" },
};

const DiffDisplay: React.FC<{ diff: number | null }> = ({ diff }) => {
  if (diff === null) {
    return <span className="text-gray-400 text-sm tabular-nums">—</span>;
  }

  if (diff === 0) {
    return (
      <span className="text-gray-500 text-sm tabular-nums font-medium">
        ±0.00
      </span>
    );
  }

  const isPositive = diff > 0;
  return (
    <span
      className={cn(
        "text-sm tabular-nums font-medium",
        isPositive ? "text-green-600" : "text-red-600"
      )}
      data-testid="diff-display"
      data-positive={isPositive}
    >
      {isPositive ? "+" : ""}
      {diff.toFixed(2)}
    </span>
  );
};

export const SupplierAwardCard: React.FC<SupplierAwardCardProps> = ({
  supplier,
  awardStatus,
  comment,
  isLeader,
  isBelowThreshold,
  showAwardDecision,
}) => {
  const displayScore =
    supplier.consensus_weighted_total ?? supplier.pre_meeting_weighted_average;
  const statusBadge = AWARD_STATUS_BADGE[awardStatus];

  return (
    <div
      className={cn(
        "rounded-lg border p-4 transition-opacity",
        isLeader
          ? "border-blue-300 bg-blue-50"
          : "border-gray-200 bg-white",
        isBelowThreshold && "opacity-60"
      )}
      data-testid={`supplier-award-card-${supplier.invite_id}`}
    >
      <div className="flex items-start justify-between gap-4 mb-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <CompanyLogoDisplay size="xxl" supplierLogoUrl={supplier.supplier_logo} supplierName={supplier.supplier_name} />
            <h3 className="text-sm font-semibold text-gray-900 truncate">
              {supplier.supplier_name}
            </h3>
            {isLeader && (
              <span className="inline-flex items-center rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700">
                Top Scorer
              </span>
            )}
            {supplier.consensus_weighted_total === null && (
              <span className="inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700">
                Pre-meeting avg
              </span>
            )}
          </div>
        </div>

        <div className="flex items-center gap-3 shrink-0">
          <div className="text-right">
            <div className="text-xs text-gray-500 mb-0.5">Weighted Total</div>
            <div className="text-lg font-semibold tabular-nums text-gray-900">
              {formatScore(displayScore)}
            </div>
          </div>

          {supplier.diff !== null && supplier.consensus_weighted_total !== null && (
            <div className="text-right">
              <div className="text-xs text-gray-500 mb-0.5">vs Pre-meeting</div>
              <DiffDisplay diff={supplier.diff} />
            </div>
          )}
        </div>
      </div>

      {showAwardDecision && (
        <div
          className="space-y-3 pt-3 border-t border-gray-200"
          data-testid={`award-decision-${supplier.invite_id}`}
        >
          <div>
            <p className="text-xs font-medium text-gray-600 mb-2">Award Decision</p>
            <span
              className={cn(
                "inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium",
                statusBadge.className
              )}
              data-testid={`award-status-badge-${supplier.invite_id}`}
            >
              {statusBadge.label}
            </span>
          </div>

          {comment && (
            <div>
              <p className="text-xs font-medium text-gray-600 mb-1">Comment</p>
              <p
                className="text-sm text-gray-700 whitespace-pre-wrap"
                data-testid={`award-comment-${supplier.invite_id}`}
              >
                {comment}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
